import { debounce } from "lodash"
import { FormValues, LoanComputationResult, RentalLoanComputationResult } from '../types';
import { getOnboardingStepTitle, ONBOARDING_STEP_CREDIT_SCORE_KEY, STATES } from './constants';

const PRICER_URL = process.env.REACT_APP_PRICER_URL;

const DEBOUNCE_DELAY_MS = 500

const controllers: AbortController[] = []
const buydownValues: number[] = [0];

export const normalizeQuotes = (value?: string | number | null): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  const asString = typeof value === 'number' ? String(value) : value;

  return asString.replace(/\u2019/g, "'").replace(/\u2018/g, "'");
};

export const loan_computation = debounce(async (values: FormValues, cb: (data: LoanComputationResult[]) => void) => {
  const creditScoreStepText = getOnboardingStepTitle(ONBOARDING_STEP_CREDIT_SCORE_KEY);

  const originalValues = {
    "What condition is the property in?": values["What condition is the property in?"] || (values.propertyRehab === "No" ? "It's stabilized and/or ready to rent" : "It needs some repairs and/or construction"),
    "What is your ideal loan length?": values["What is your ideal loan length?"] || (values.propertyRehab ? "Short-term (12 months)" : "Long-term (30 years)"),
    "What's your credit score?": values["What's your credit score?"] || values.creditScore,
    "How many properties have you purchased/sold in the last 24 months?": values["How many properties have you purchased/sold in the last 24 months?"] || "1-2 properties",
    "Where is the property located?": values["Where is the property located?"] || "Alabama",
    "What kind of property is it?": values["What kind of property is it?"] || "2-4 unit property",
    "Do you currently own this property?": values["Do you currently own this property?"] || (values.refinance === "yes" ? "Yes, I want to refinance" : "No, I'm looking to purchase")
  }

  // Check if this is a rental loan - if so, this function should not be called
  // The caller should use rental_loan_computation instead
  // Check both originalValues and the raw values to catch edge cases
  const isRental = isLongTermRental(originalValues) || isLongTermRental(values)
  
  // Additional check: if condition is stabilized and length is long-term, it's a rental
  const conditionRaw = originalValues["What condition is the property in?"] || values["What condition is the property in?"]
  const lengthRaw = originalValues["What is your ideal loan length?"] || values["What is your ideal loan length?"]
  const condition = normalizeQuotes(conditionRaw)?.trim()
  const length = normalizeQuotes(lengthRaw)?.trim()
  const isRentalByCondition = condition === "It's stabilized and/or ready to rent" && 
                               length === "Long-term (30 years)"
  
  if (isRental || isRentalByCondition) {
    console.warn("loan_computation called for a rental loan. This should use rental_loan_computation instead.", {
      condition: conditionRaw,
      length: lengthRaw,
      isRental,
      isRentalByCondition,
      values: Object.keys(values)
    })
    return cb([])
  }

  const isBridge = isShortTermBridge(originalValues)
  const isFixAndFlip = isShortTermFixAndFlip(originalValues)
  
  if (isBridge) {
    const mapInputs = (oldInputs: FormValues, buydown: number): any => {
      const investmentExperience = (oldInputs["How many properties have you purchased/sold in the last 24 months?"] as string).split(" ")[0]
      const newInputs = {
        "borrower_credit_score": oldInputs[creditScoreStepText] || oldInputs["What’s your credit score?"] || oldInputs.creditScore,
        "borrower_24_months_experience": investmentExperience === "None" ? "0" : investmentExperience,
        "as_is_value_purchase_price": oldInputs.price !== undefined ? oldInputs.price : (oldInputs.asIsValue !== undefined ? oldInputs.asIsValue : (oldInputs["What's the purchase price?"] || oldInputs["What's the as-is value?"] || 0)),
        "if_refi_payoff_amount": oldInputs.currentLoanBalance || 0,
        "buydown_in_pts": buydown,
        ...(
          oldInputs.loanBalance ?
            {
              "initial_loan_amount": oldInputs.loanBalance 
            }
            :
            {}
        )
      }
  
      return newInputs
    }
  
    const mapOutputs = (outputs: any): any => {
      const oldOutputs = {
        "rate": outputs.rate,
        "advance_amount_max": outputs.advance_amount_max
      }
  
      return {...oldOutputs, ...outputs}
    }
  
    const isOptimize = !mapInputs(values, 0).initial_loan_amount
    const pricerUrl = `${PRICER_URL}/bridge${isOptimize ? "?multiple=True&optimize=True" : "?multiple=True"}`
  
    const fetchPromisesGetter = buydownValues.map(buydownValue => {
      return () => {
        const controller = new AbortController();
        const signal = controller.signal;
        controllers.push(controller);
    
        return fetch(pricerUrl, {
          method: 'post',
          body: JSON.stringify(mapInputs(values, buydownValue)),
          headers: {
            'Content-Type': 'application/json'
          },
          signal
        })
        .then(res => res.json())
        .then(data => Array.isArray(data.data) ? data.data.map(mapOutputs) : mapOutputs(data.data))
        .finally(() => {
          const index = controllers.indexOf(controller);
          if (index !== -1) {
            controllers.splice(index, 1);
          }
        })
      }
    });
  
    return new Promise(() => {
      controllers.forEach(controller => controller.abort());
      Promise
        .all(fetchPromisesGetter.map((f) => f()))
        .then((data) => cb(data))
        .catch(() => cb([null as any]))
    });
  } else {
    const mapInputs = (oldInputs: any, buydown: number) => {
      const investmentExperience = oldInputs["How many properties have you purchased/sold in the last 24 months?"].split(" ")[0]
      const newInputs = {
        "borrower_credit_score": oldInputs[creditScoreStepText] || oldInputs["What’s your credit score?"] || oldInputs.creditScore,
        "borrower_24_months_experience": investmentExperience === "None" ? "0" : investmentExperience,
        "as_is_value_purchase_price": oldInputs.price !== undefined ? oldInputs.price : (oldInputs.asIsValue !== undefined ? oldInputs.asIsValue : (oldInputs["What's the purchase price?"] || oldInputs["What's the as-is value?"] || 0)),
        "if_refi_payoff_amount": oldInputs.currentLoanBalance || 0,
        "rehab_budget": oldInputs.constructionBudget,
        "arv": oldInputs.afterRepairValue,
        "buydown_in_pts": buydown,
        ...(
          oldInputs.loanBalance ?
            {
              "total_loan_amount": oldInputs.loanBalance
            }
            :
            {}
        )
      }
  
      return newInputs
    }
  
    const mapOutputs = (outputs: any) => {
      const oldOutputs = {
        "rate": outputs.rate,
        "advance_amount_max": outputs.advance_amount_max,
        "construction_reserve_max": outputs.construction_reserve
      }
  
      return {...oldOutputs, ...outputs}
    }
  
    // Double-check this is not a rental loan before calling fix_and_flip
    const finalCheckConditionRaw = originalValues["What condition is the property in?"] || values["What condition is the property in?"]
    const finalCheckLengthRaw = originalValues["What is your ideal loan length?"] || values["What is your ideal loan length?"]
    const finalCheckCondition = normalizeQuotes(finalCheckConditionRaw)?.trim()
    const finalCheckLength = normalizeQuotes(finalCheckLengthRaw)?.trim()
    const isRentalFinalCheck = finalCheckCondition === "It's stabilized and/or ready to rent" && 
                               finalCheckLength === "Long-term (30 years)"
    
    if (isRentalFinalCheck) {
      console.error("ERROR: loan_computation attempting to call fix_and_flip for a rental loan. This should not happen.", {
        condition: finalCheckConditionRaw,
        length: finalCheckLengthRaw,
        values: Object.keys(values)
      })
      return cb([])
    }

    const isOptimize = !mapInputs(values, 0).total_loan_amount
    const pricerUrl = `${PRICER_URL}/fix_and_flip${isOptimize ? "?multiple=True&optimize=True" : "?multiple=True"}`

    const fetchPromisesGetter = buydownValues.map(buydownValue => {
      return () => {
        const controller = new AbortController();
        const signal = controller.signal;
        controllers.push(controller);
    
        return fetch(pricerUrl, {
          method: 'post',
          body: JSON.stringify(mapInputs(values, buydownValue)),
          headers: {
            'Content-Type': 'application/json'
          },
          signal
        })
        .then(res => res.json())
        .then(data => Array.isArray(data.data) ? data.data.map(mapOutputs) : mapOutputs(data.data))
        .finally(() => {
          const index = controllers.indexOf(controller);
          if (index !== -1) {
            controllers.splice(index, 1);
          }
        })
      }
    });
  
    return new Promise(() => {
      controllers.forEach(controller => controller.abort());
      Promise
        .all(fetchPromisesGetter.map((f) => f()))
        .then((data) => cb(data))
        .catch(() => cb([null as any]))
    });
  }
}, DEBOUNCE_DELAY_MS)

export const rental_loan_computation = debounce(async (values: FormValues, cb: (data: RentalLoanComputationResult[]) => void) => {
  const creditScoreStepText = getOnboardingStepTitle(ONBOARDING_STEP_CREDIT_SCORE_KEY);
  
  function mapInputs(oldInputs: FormValues, buydown: number): any {
    // Ensure propertyType is set - if not, try to get it from the property kind mapping
    let propertyType = oldInputs.propertyType
    if (!propertyType && oldInputs["What kind of property is it?"]) {
      propertyType = PROPERTY_TYPE[oldInputs["What kind of property is it?"] as keyof typeof PROPERTY_TYPE]
    }
    
    const newInputs = {
      program: oldInputs.program,
      property_type: propertyType,
      loan_purpose: isRefinance(oldInputs) ? "Refinance" : "Purchase",
      fico: oldInputs[creditScoreStepText] || oldInputs["What’s your credit score?"] || oldInputs.creditScore,
      buydown: buydown || 0,
      price: oldInputs.price || 0,
      payoff: oldInputs.currentLoanBalance || 0,
      monthly_market_rent: oldInputs.monthlyRent || 0,
      annual_property_taxes: oldInputs.annualTaxes || 0,
      annual_insurance: oldInputs.annualInsurance || 0,
      annual_hoa: oldInputs.annualHoa || 0,
      ...(
        oldInputs.loanBalance ?
          {
            loan_balance: oldInputs.loanBalance 
          }
          :
          {}
      )
    }

    return newInputs
  }

  const mapOutputs = (outputs: any) => {
    const oldOutputs = {
      "buydown_fee_usd": 0,
      "can_be_thirty_year_fixed_loan": true,
      "loan_amount": outputs.loan_balance,
      "ltv": outputs.ltv,
      "monthly_payment": outputs.monthly_payment,
      "total_rate": outputs.rate,
      "dscr": outputs.dscr,
      "max_ltv": outputs.max_ltv,
      "cashout_amount": outputs.cashout_amount,
      "bond_price": outputs.bond_price,
      "is_extended": outputs.is_extended,
      "is_plus": outputs.is_plus,
      "is_flex": outputs.is_flex,
      "min_ltv": outputs.min_ltv,
    }

    return {...oldOutputs}
  }
  
  const isOptimize = !mapInputs(values, 0).loan_balance
  const pricerUrl = `${PRICER_URL}/rental${isOptimize ? "?multiple=True&optimize=True" : "?multiple=True"}`

  const fetchPromisesGetter = buydownValues.map(buydownValue => {
    return () => {
      const controller = new AbortController();
      const signal = controller.signal;
      controllers.push(controller);
  
      return fetch(pricerUrl, {
        method: 'post',
        body: JSON.stringify(mapInputs(values, buydownValue)),
        headers: {
          'Content-Type': 'application/json'
        },
        signal
      })
      .then(res => res.json())
      .then(data => data.data.map(mapOutputs))
      .finally(() => {
        const index = controllers.indexOf(controller);
        if (index !== -1) {
          controllers.splice(index, 1);
        }
      })
    }
  });

  return new Promise(() => {
    controllers.forEach(controller => controller.abort());
    Promise
      .all(fetchPromisesGetter.map((f) => f()))
      .then((data) => cb(data))
      .catch(() => cb([]))
  });
}, DEBOUNCE_DELAY_MS)

/* Input mapping */
const CREDIT_SCORE = {
  ["Less than 660"]: "Less than 660",
  ["660-679"]: "660-679",
  ["680-699"]: "680-699",
  ["700-719"]: "700-719",
  ["720-739"]: "720-739",
  ["740-759"]: "740-759",
  ["760-779"]: "760-779",
  ["780+"]: "780+"
}

const PROPERTY_KIND = {
  ["Single family"]: "Single Family/Condo",
  ["Portfolio"]: "None of these",
  ["Warrantable condo"]: "None of these",
  ["Non-warrantable condo"]: "None of these",
  ["2-4 unit property"]: "2-4 Unit",
  ["5-20 unit property"]: "5+ Multifamily",
}

const PROPERTY_TYPE = {
  ["Single family"]: "Single Family",
  ["Portfolio"]: "Warrantable Condo",
  ["Warrantable condo"]: "Warrantable Condo",
  ["Non-warrantable condo"]: "Non-Warrantable Condo",
  ["2-4 unit property"]: "2-4 Unit",
  ["5-20 unit property"]: "5-20 Unit",
  ["Mixed-use property"]: "Mixed Use"
}

const COMPLETED_PROJECTS = {
  ["None"]: 0,
  ["1-3 properties"]: 2,
  ["3-6 properties"]: 4,
  ["6+ properties"]: 7,
}

/**
 * This function converts the values that were obtained from the application
 * questionary to the old object shape that was used in the original pricer
 * 
 * @param {object} inputs The result of the questionary
 */
export const mapInput = (inputs: any) => {
  const creditScoreStepText = getOnboardingStepTitle(ONBOARDING_STEP_CREDIT_SCORE_KEY);
  
  let out = {...inputs}
  /* Mapping values */
  out['creditScore'] = inputs[creditScoreStepText] || inputs["What’s your credit score?"] || inputs.creditScore
  out['creditScore'] = CREDIT_SCORE[out['creditScore'] as keyof typeof CREDIT_SCORE]

  out['state'] = inputs['Where is the property located?']
  out['state'] = STATES[out['state'] as keyof typeof STATES]

  out['propertyKind'] = inputs['What kind of property is it?']
  out['propertyKind'] = PROPERTY_KIND[out['propertyKind'] as keyof typeof PROPERTY_KIND]

  out['completedProjects'] = inputs['How many properties have you purchased/sold in the last 24 months?']
  out['completedProjects'] = COMPLETED_PROJECTS[out['completedProjects'] as keyof typeof COMPLETED_PROJECTS]

  out['asIsValue'] = inputs['price']
  out['price'] = inputs['price']

  /* Adding missing values */
  if (isRefinance(inputs)) {
    out['refinance'] = 'yes'
    out['price'] = inputs['price']
    out['asIsValue'] = inputs['price']
  } else {
    out['refinance'] = 'no'
  }
  
  if (isShortTermBridge(inputs)) {
    out = {...out, "propertyRehab": "No" }
  }

  if (isShortTermFixAndFlip(inputs)) {
    out['constructionBudget'] = inputs['constructionBudget']
    out['afterRepairValue'] = inputs['afterRepairValue']

    out = {...out, "propertyRehab": "Yes", "customLTC": "Max" }
  }

  if (isLongTermRental(inputs)) {
    out["purchaseRefinance"] = out["refinance"]

    out = {
      ...out,
      "propertyState": out['state'],
      "propertyType": PROPERTY_TYPE[inputs['What kind of property is it?'] as keyof typeof PROPERTY_TYPE]
    }
  }

  return out
}

/* Utils */
export const isShortTermBridge = (values: FormValues): boolean => {
  const conditionValue = values["What condition is the property in?"]
  const lengthValue = values["What is your ideal loan length?"]
  const expectedCondition = "It’s stabilized and/or ready to rent"
  const expectedLength = "Short-term (12 months)"
  
  const conditionCheck = conditionValue === expectedCondition
  const lengthCheck = lengthValue === expectedLength
   
  return conditionCheck && lengthCheck
}

export const isShortTermFixAndFlip = (values: FormValues): boolean => {
  const conditionCheck = values["What condition is the property in?"] === "It needs some repairs and/or construction"
  const lengthCheck = values["What is your ideal loan length?"] === "Short-term (12 months)"
  
  return conditionCheck && lengthCheck
}

export const isLongTermRental = (values: FormValues): boolean => {
  const condition = normalizeQuotes(values["What condition is the property in?"])?.trim()
  const length = normalizeQuotes(values["What is your ideal loan length?"])?.trim()
  
  // Check both conditions explicitly
  const conditionMatch = condition === "It's stabilized and/or ready to rent"
  const lengthMatch = length === "Long-term (30 years)"
  
  return conditionMatch && lengthMatch
}

export const isRefinance = (values: FormValues): boolean => (values["Do you currently own this property?"] === "Yes, I want to refinance")

export const isSingleHome = (values: FormValues): boolean => (values["What kind of property is it?"] === "Single family")

export const isTwoToFourUnit = (values: FormValues): boolean => (values["What kind of property is it?"] === "2-4 unit property")

export const isFiveToTwentyUnit = (values: FormValues): boolean => (values["What kind of property is it?"] === "5-20 unit property")

export const isMixedUseProperty = (values: FormValues): boolean => (values["What kind of property is it?"] === "Mixed-use property")

export const isLessThan660 = (values: FormValues): boolean => {
  const creditScoreStepText = getOnboardingStepTitle(ONBOARDING_STEP_CREDIT_SCORE_KEY);
  return ((values[creditScoreStepText] || values["What’s your credit score?"] || values.creditScore) === "Less than 660");
}

export const cannotBePriced = (values: FormValues): boolean => (
  (isLongTermRental(values) && isFiveToTwentyUnit(values))
  ||
  (isMixedUseProperty(values))
  ||
  (isLessThan660(values))
  // ||
  // (isLongTermRental(values) && (ONBOARDING_STEPS.find(o => o.shortKey === ONBOARDING_STEP_CREDIT_SCORE_KEY).options.findIndex((option) => option.text === values[ONBOARDING_STEPS.find(o => o.shortKey === ONBOARDING_STEP_CREDIT_SCORE_KEY).text]) < 2))
)

export const isPortfolio = (values: FormValues): boolean => (values["What kind of property is it?"] === "Portfolio")

/**
 * Removes the country (typically "USA") from the end of an address string.
 * This function removes the last comma-separated segment from the address.
 * 
 * @param address - The address string to format
 * @returns The address without the country suffix, or empty string if address is falsy
 * 
 * @example
 * formatAddressWithoutCountry("123 Main St, Los Angeles, CA, USA") // "123 Main St, Los Angeles, CA"
 * formatAddressWithoutCountry("California, USA") // "California"
 */
export const formatAddressWithoutCountry = (address: string): string => {
  return address.replace(/, [^,]*$/, "")
}